import { useMemo, useState } from "react"
import { Alert } from "@mui/material"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"

import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { StandardForm } from "../../../../../../shared/components/forms/StandardForm"
import { StandardMainPanel } from "../../../../../../shared/components/forms/StandardMainPanel"
import { StandardSidePanel } from "../../../../../../shared/components/forms/StandardSidePanel"
import { StandardFormTabs } from "../../../../../../shared/components/tabs/StandardFormTabs"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { User } from "../../../../../users/userTypes"
import {
    CompanyAdminDepartmentsQuery,
    DepartmentsForCompanyResponse,
    fetchDepartmentsForCompany,
} from "../../departments/departmentsApi"
import {
    CompanyAdminUserGroupsQuery,
    UserGroupsForCompanyResponse,
    fetchUserGroupsForCompany,
} from "../../userGroups/userGroupsApi"
import {
    CompanyAdminWorkgroupsQuery,
    WorkgroupsForCompanyResponse,
    fetchWorkgroupsForCompany,
} from "../../workgroups/workgroupsApi"
import { useCurrentCompany } from "../../../../hooks/useCurrentCompany"

import {
    CompanyMeetingServicePriceListsQuery,
    CompanyMeetingServicePriceListsResponse,
    fetchCompanyMeetingServicePriceLists,
} from "../companyPriceListsApi"
import { CompanyUserFormMode, CompanyUserFormValues, USER_LANGUAGE_OPTIONS } from "../companyUserFormTypes"
import { CompanyUserSettingsPanel } from "./CompanyUserSettingsPanel"
import { ApprovalsTab } from "./tabs/ApprovalsTab"
import { CatalogAccessTab } from "./tabs/CatalogAccessTab"
import { GuestCheckInTab } from "./tabs/GuestCheckInTab"
import { UserDetailsTab } from "./tabs/UserDetailsTab"
import { WorkgroupsTab } from "./tabs/WorkgroupsTab"

type CreateEditUserContentProps = Readonly<{
    mode: CompanyUserFormMode
    existingUser?: User
}>

const useStyles = makeStyles()((theme) => ({
    syncWarning: {
        marginBottom: theme.spacing(3),
    },
}))

const selectUserGroupOptions = (data: UserGroupsForCompanyResponse) =>
    data.userGroups.map((group) => ({ id: group.id, name: group.name }))

const selectDepartmentOptions = (data: DepartmentsForCompanyResponse) =>
    data.departments.map((department) => ({ id: department.id, name: department.name }))

const selectPriceListMerchants = (data: CompanyMeetingServicePriceListsResponse) => data.merchants

const selectWorkgroups = (data: WorkgroupsForCompanyResponse) => data.workgroups

export function CreateEditUserContent({ mode }: CreateEditUserContentProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { companyId } = useCurrentCompany()
    const { values } = useFormikContext<CompanyUserFormValues>()

    const [activeTab, setActiveTab] = useState(0)

    const userGroupsResult = useApiQuery(fetchUserGroupsForCompany, {
        queryName: CompanyAdminUserGroupsQuery.LIST,
        dependencies: { companyId },
        select: selectUserGroupOptions,
    })
    const departmentsResult = useApiQuery(fetchDepartmentsForCompany, {
        queryName: CompanyAdminDepartmentsQuery.LIST,
        dependencies: { companyId },
        select: selectDepartmentOptions,
    })

    const priceListsResult = useApiQuery(fetchCompanyMeetingServicePriceLists, {
        queryName: CompanyMeetingServicePriceListsQuery.LIST,
        dependencies: { companyId },
        select: selectPriceListMerchants,
    })

    const workgroupsResult = useApiQuery(fetchWorkgroupsForCompany, {
        queryName: CompanyAdminWorkgroupsQuery.LIST,
        dependencies: { companyId },
        select: selectWorkgroups,
    })

    const userGroups = userGroupsResult.data ?? []
    const departments = departmentsResult.data ?? []
    const priceListMerchants = priceListsResult.data ?? []
    const workgroups = workgroupsResult.data ?? []

    // Sync-managed users have their name/email/department overwritten by the sync job unless sync is disabled.
    const isSyncManaged = mode === "update" && !values.disableUserSync

    const tabs = useMemo(
        () => [
            {
                label: l10n.getString("company-user-tab-details"),
                component: (
                    <UserDetailsTab
                        userGroups={userGroups}
                        departments={departments}
                        languageOptions={USER_LANGUAGE_OPTIONS}
                    />
                ),
            },
            {
                label: l10n.getString("company-user-tab-workgroups"),
                badge: values.workgroups.length,
                component: <WorkgroupsTab workgroups={workgroups} isLoading={workgroupsResult.isInitialLoading} />,
            },
            {
                label: l10n.getString("company-user-tab-approvals"),
                badge: values.approverForUserGroupIds.length,
                component: <ApprovalsTab userGroups={userGroups} />,
            },
            {
                label: l10n.getString("company-user-tab-catalog"),
                badge: values.assignedPriceListIds.length,
                component: (
                    <CatalogAccessTab merchants={priceListMerchants} isLoading={priceListsResult.isInitialLoading} />
                ),
            },
            {
                label: l10n.getString("company-user-tab-guest-checkin"),
                badge: values.notifyOnGuestArrival ? 1 : 0,
                component: <GuestCheckInTab />,
            },
        ],
        [
            l10n,
            userGroups,
            departments,
            priceListMerchants,
            priceListsResult.isInitialLoading,
            workgroups,
            workgroupsResult.isInitialLoading,
            values.workgroups.length,
            values.approverForUserGroupIds.length,
            values.assignedPriceListIds.length,
            values.notifyOnGuestArrival,
        ],
    )

    return (
        <StandardForm>
            <StandardMainPanel>
                {isSyncManaged && (
                    <LocalizedStrict id="company-user-sync-warning">
                        <Alert severity="info" className={classes.syncWarning}>
                            This user is kept in directory sync — name, email and department may be overwritten by the
                            next sync. Disable user sync to edit them permanently.
                        </Alert>
                    </LocalizedStrict>
                )}
                <StandardFormTabs tabs={tabs} value={activeTab} onChange={setActiveTab} />
            </StandardMainPanel>

            <LocalizedStrict id="company-user-settings-panel-title" attrs={{ title: true }}>
                <StandardSidePanel title="Settings">
                    <CompanyUserSettingsPanel mode={mode} />
                </StandardSidePanel>
            </LocalizedStrict>
        </StandardForm>
    )
}
